import { Link } from "react-router-dom";
import { Phone, CheckCircle, Users, Wrench, Award, Medal, Shield, Clock } from "lucide-react";
import familyPhoto from "@/assets/family-photo.png";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { PHONE, PHONE_LINK, BUSINESS_NAME, BUSINESS_TAGLINE, ORG_SCHEMA } from "@/lib/constants";

const About = () => {
  const baseUrl = "https://truamericanplumbing.com";

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: baseUrl },
      { "@type": "ListItem", position: 2, name: "About", item: `${baseUrl}/about` },
    ],
  };

  return (
    <>
      <SEO
        title="About Us - Your Local West Valley Plumber"
        description="Learn about Tru American Plumbing Purifications, your trusted local plumber in Tolleson, AZ. Licensed, bonded, insured. Serving the West Valley with integrity."
        canonicalUrl="/about/"
        schema={[ORG_SCHEMA, aboutSchema]}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 rounded-full px-4 py-2 mb-6">
              <Medal className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">{BUSINESS_TAGLINE}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About <span className="text-gradient-copper">{BUSINESS_NAME}</span>
            </h1>
            <p className="text-xl text-muted-foreground">
              Your trusted local plumber serving Tolleson and the West Valley with
              integrity, expertise, and genuine care for our community.
            </p>
          </div>
        </div>
      </section>

      {/* Meet the Owner */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Meet the <span className="text-gradient-copper">Owner</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-10 max-w-5xl mx-auto items-center">
            <div className="flex justify-center">
              <img
                src={familyPhoto}
                alt="Thomas and his family — owner of Tru American Plumbing Purifications"
                className="rounded-xl border-2 border-primary/30 shadow-lg w-full max-w-md object-cover"
              />
            </div>
            <div className="space-y-5">
              <p className="text-foreground/80 text-lg leading-relaxed">
                My name is Thomas, and I am the proud owner of Tru American Plumbing Purifications. I am also the proud father of four beautiful girls and grateful for my amazing wife, who has helped me build and grow our company from the ground up. And yes, we are counting our dog Snow, so I am definitely outnumbered in my household—but I wouldn't have it any other way.
              </p>
              <p className="text-foreground/80 text-lg leading-relaxed">
                A little about my background: I proudly served in the United States Army as a 91B Wheeled Vehicle Mechanic. The military instilled values in me that I carry into my business every day—hard work, integrity, discipline, and getting the job done right the first time.
              </p>
              <p className="text-foreground/80 text-lg leading-relaxed">
                With over 10 years of experience in the plumbing and water treatment industry, our mission at Tru American Plumbing Purifications is to provide high-quality products and dependable workmanship. We take pride in making sure our customers are well taken care of, and we treat every home with the same respect and care as if it were our own.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <div className="prose prose-invert max-w-none space-y-4 text-foreground/80">
              <p>
                {BUSINESS_NAME} is a veteran-owned and family-operated business 
                founded with a simple mission: to provide honest, reliable plumbing 
                services to our neighbors in the West Valley. We believe that every 
                homeowner deserves a plumber they can trust – one who shows up on time, 
                explains the problem clearly, and does the job right the first time.
              </p>
              <p>
                Built on the values of service, integrity, and American quality that 
                come from our military background, we bring the same dedication and 
                discipline to every plumbing job. Based right here in Tolleson, Arizona, 
                we understand the unique challenges that Arizona homes face. From the 
                extreme hard water that damages pipes and appliances to the summer heat 
                that strains water heaters, we've seen it all and know how to fix it.
              </p>
              <p>
                We're committed to protecting your home from Arizona's harsh water. 
                Whether it's emergency pipe repairs, slab leak detection, water heater 
                installs, or specialized water softener and reverse osmosis systems, 
                we handle your home's complete plumbing health.
              </p>
              <p>
                We're not just another plumbing company – we're your neighbors. When
                you call us, you're supporting a local, veteran-owned business that 
                cares about this community. We take pride in building lasting 
                relationships with our customers, many of whom have become repeat 
                clients and friends.
              </p>
            </div>

            {/* 24/7 Scheduling Note */}
            <div className="mt-8 p-6 bg-card border border-border rounded-xl flex gap-4">
              <Clock className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg mb-2">24/7 Intelligent Scheduling</h3>
                <p className="text-muted-foreground">
                  Need to book a repair or get support? Our advanced voice assistant 
                  is available 24/7 to help you schedule service at your convenience.
                </p>
              </div>
            </div>

            {/* Veteran Discount Card */}
            <div className="mt-4 p-6 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/30 rounded-xl flex gap-4">
              <Medal className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-lg mb-2">Honoring Those Who Serve</h3>
                <p className="text-muted-foreground mb-3">
                  As a veteran-owned business, we proudly offer special discounts to 
                  veterans, active-duty military, and first responders. It's our way 
                  of giving back to those who serve our country and community.
                </p>
                <a href={PHONE_LINK}>
                  <Button variant="hero" size="sm" className="gap-2">
                    <Phone className="h-4 w-4" />
                    Call to Claim Your Discount
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">
            What We <span className="text-gradient-copper">Stand For</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 gradient-copper rounded-xl flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">Honest Pricing</h3>
              <p className="text-muted-foreground">
                Upfront quotes with no hidden fees. We respect your budget.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 gradient-copper rounded-xl flex items-center justify-center mx-auto mb-4">
                <Medal className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">American Quality</h3>
              <p className="text-muted-foreground">
                Built on military values of service and excellence. We never cut corners.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 gradient-copper rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">Protecting Your Home</h3>
              <p className="text-muted-foreground">
                Defending Arizona homes from harsh water and plumbing problems.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Licensed & <span className="text-gradient-copper">Insured</span>
            </h2>
            <div className="space-y-4">
              <div className="flex items-center gap-4 bg-card border border-border rounded-xl p-6">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-bold">Licensed Plumbing Contractor</h3>
                  <p className="text-sm text-muted-foreground">
                    State of Arizona licensed plumber
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-card border border-border rounded-xl p-6">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-bold">Bonded</h3>
                  <p className="text-sm text-muted-foreground">
                    Financial protection for every project
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 bg-card border border-border rounded-xl p-6">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-bold">Fully Insured</h3>
                  <p className="text-sm text-muted-foreground">
                    Complete liability coverage protects you and your property
                  </p>
                </div>
              </div>
              <a href="https://www.bbb.org/us/az/tolleson/profile/plumber/tru-american-plumbing-purifications-llc-1126-1000166640" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-colors">
                <CheckCircle className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <h3 className="font-bold">BBB Accredited Business</h3>
                  <p className="text-sm text-muted-foreground">
                    A- rated by the Better Business Bureau — accredited since March 2026
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-primary/20 via-background to-background">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Work With Us?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Experience the Tru American difference. Contact us today for honest,
            reliable plumbing service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={PHONE_LINK}>
              <Button variant="hero" size="lg" className="gap-2">
                <Phone className="h-4 w-4" />
                {PHONE}
              </Button>
            </a>
            <Link to="/contact/">
              <Button variant="outline" size="lg">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default About;
